import { subscribe } from "valtio/vanilla";
import { ProjectsDocument, ProjectsQuery, ProjectsQueryVariables } from "@gql/sites-api/graphql";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo/routedClient";
import { siteStore } from "../store";

const SELECTED_PROJECT_LOCAL_STORAGE_KEY = "selectedProjectId";

function persistSelectedProjectIdToLocalStorage() {
  if (siteStore.selectedProjectId) {
    localStorage.setItem(SELECTED_PROJECT_LOCAL_STORAGE_KEY, siteStore.selectedProjectId);
  }
}

/**
 * We want to update the selected project in localStorage whenever the selected
 * project in store changes OR when the tab is focussed. The latter effect is
 * necessary to ensure that when refreshing the current tab, the project does
 * not change unexpectedly.
 *
 * @todo handle current project being archived from another tab
 */
subscribe(siteStore, persistSelectedProjectIdToLocalStorage);
addEventListener("focus", persistSelectedProjectIdToLocalStorage);

/**
 * Reconciles the selected project from network, localStorage, and store.
 *
 * Orders of precedence:
 *   1. Project in store (validated against projects from network)
 *   2. Project in localStorage (validated against projects from network)
 *   3. Last opened project from network
 *
 * @todo handle no projects found for user
 */
export async function loadSelectedProject() {
  try {
    const {
      data: { projects },
    } = await routedClient.query<ProjectsQuery, ProjectsQueryVariables>({
      query: ProjectsDocument,
      fetchPolicy: "network-only",
      context: {
        endpoint: LandTechEndpoints.Sites,
      },
    });

    /**
     * If a project is already in memory prioritise that over the local storage
     * value. This allows for multiple tabs to use distinct projects.
     */
    const projectIdFromMemory = siteStore.selectedProjectId;
    const projectIdFromLocalStorage = localStorage.getItem(SELECTED_PROJECT_LOCAL_STORAGE_KEY);
    const projectIdToUse = projectIdFromMemory || projectIdFromLocalStorage;
    const selectedProject = projects.find((project) => project.id === projectIdToUse);

    siteStore.selectedProjectId = selectedProject?.id ?? projects[0]?.id ?? null;
  } catch (error) {
    console.error(error);
  }
}
